import React, { useState } from "react";
import { Alert } from "@mui/material";
import { FileMosaic } from "@files-ui/react";
import { useMutation } from "@apollo/client/react";
import OverwatchModal from "@/components/OverwatchModal";
import { BulkImportCompanyEquipmentsDocument, GetCompanyResourcesDocument, CompanyResourceResourceTypeEnum } from "@/graphql/generated/graphql";
import useDirectUpload from "@/hooks/useDirectUpload";
import { StyledDropzone } from "@/theme/styled-components";
import { displayNotification } from "@/utils/notifications";

interface CreateEquipmentModalProps {
  handleClose: () => void;
}
const UploadEquipmentsModal: React.FC<CreateEquipmentModalProps> = ({
  handleClose,
}) => {
  // this was supposed to be ExtFile[] but ExtFile is not working for some reason.
  const [files, setFiles] = useState<any[]>([]);
  const { isUploading, uploadFile, error } = useDirectUpload();
  const [bulkImport, { loading }] = useMutation(BulkImportCompanyEquipmentsDocument);

  const handleFileChange = (files: any[]) => {
    setFiles(files);
  };

  const handleSubmit = () => {
    if (files) {
      uploadFile(files[0].file as File, "imports/company-equipments")
        .then((response) => {
          bulkImport({
            variables: { signedId: response?.signedBlobId as string },
            onCompleted: () => {
              displayNotification({
                type: "success",
                text: "Company Equipments created successfully",
              });
              handleClose();
            },
            onError: () => {
              displayNotification({
                type: "error",
                text: "Unable to create the equipments. Try again later.",
              });
            },
            refetchQueries: [
              {
                query: GetCompanyResourcesDocument,
                variables: { resourceType: CompanyResourceResourceTypeEnum.Equipment },
              },
            ],
          });
        });
    }
  };

  const removeFile = (id: string | number | undefined) => {
    setFiles(files.filter((x) => x.id !== id));
  };

  const handleTertiaryClick = () => {
    window.open("https://resources-management.s3.eu-west-2.amazonaws.com/resource_upload_sample.csv", "_blank")
  }

  return (
    <OverwatchModal
      size="sm"
      title="Bulk Upload Equipments"
      caption="Create equipments in bulk using a CSV"
      handleClose={handleClose}
      primaryButtonAction={handleSubmit}
      primaryButtonText={isUploading ? "Uploading..." : loading ? "Importing..." : "Upload"}
      disablePrimary={!files.length || isUploading || loading}
      tertiaryButtonText="Download Sample CSV"
      tertiaryButtonAction={handleTertiaryClick}
    >
      <StyledDropzone
        onChange={handleFileChange}
        value={files}
        accept=".csv"
        maxFiles={1}
        header={false}
        footer={false}
      >
        {files.map((files) => (
          <FileMosaic key={files.id} {...files} onDelete={removeFile} info />
        ))}
      </StyledDropzone>

      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    </OverwatchModal>
  )
};

export default UploadEquipmentsModal;
